/* =====================================================================
   BYOKS — Build Your Own Kitchen Suite
   Vanilla-JS port of the Claude Design handoff (kitchen-builder-white-thumbs.jsx).
   The handoff shipped as React 18 + Babel-standalone loaded from unpkg; that's
   ~3MB of in-browser transpiler on a storefront page, so the component tree is
   reimplemented here as string templates + one delegated click handler.

   Reads window.BY_* from assets/byoks-data.js — load that first.
   Renders into #byoks-root. Header/footer come from the theme, not from here.
===================================================================== */
(function () {
  "use strict";

  var root = document.getElementById("byoks-root");
  if (!root || typeof window.BY_RANGES === "undefined") return;

  /* ── Helpers ──────────────────────────────────────────── */

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
  function fmt(n) { return "$" + Math.round(n).toLocaleString("en-US"); }
  function url(p) { return (window.BY_BASE || "") + p; }
  function img(f) { return window.BY_CDN + f; }

  var ICON = {
    arrow: '<svg class="by-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    shield: '<svg class="by-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z"/><path d="m9 12 2 2 4-4"/></svg>',
    bolt: '<svg class="by-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M13 2 4 14h7l-1 8 9-12h-7z"/></svg>'
  };

  var FINISHES = [
    { id: "ss", name: "Stainless Steel", meta: "The professional classic", swatch: "linear-gradient(135deg,#d7d5d2 0%,#b5b1ab 100%)" },
    { id: "bs", name: "Black Stainless", meta: "Modern. Low-glare.", swatch: "linear-gradient(135deg,#5a5854 0%,#312f2c 100%)" },
    { id: "vega", name: "VEGA Edition", meta: "Stainless body, gold hardware", swatch: "linear-gradient(135deg,#e2dfd8 0%,#b5b1ab 40%,#bc8b39 100%)" },
    { id: "orion", name: "ORION Edition", meta: "Black stainless body, gold hardware", swatch: "linear-gradient(135deg,#5a5854 0%,#25231f 55%,#bc8b39 100%)" }
  ];
  var FIN_LABEL = { ss: "Stainless Steel", bs: "Black Stainless", vega: "VEGA Edition", orion: "ORION Edition" };
  var FIN_FALLBACK = { ss: ["bs"], bs: ["ss"], vega: ["ss", "bs"], orion: ["bs", "ss"] };

  /* Resolve an option to the chosen finish, falling back to a related finish. */
  function variant(opt, finish) {
    if (!opt) return null;
    if (opt[finish]) return Object.assign({}, opt[finish], { finish: finish, exact: true });
    var fbs = FIN_FALLBACK[finish] || [];
    for (var i = 0; i < fbs.length; i++) {
      if (opt[fbs[i]]) return Object.assign({}, opt[fbs[i]], { finish: fbs[i], exact: false });
    }
    return null;
  }
  function byId(list, id) {
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return null;
  }
  function where(list, key, val) {
    return list.filter(function (o) { return o[key] === val; });
  }
  /* "none" first in the data, last in the UI. */
  function noneLast(list) {
    return list.filter(function (o) { return o.id !== "none"; })
      .concat(list.filter(function (o) { return o.id === "none"; }));
  }

  /* Clear any selection with no SKU (exact or fallback) in the newly chosen finish. */
  function applyFinish(b, finish) {
    function keep(list, id, fallback) {
      if (id === "none") return "none";
      var o = byId(list, id);
      return o && variant(o, finish) ? id : fallback;
    }
    var cur = byId(BY_FRIDGES, b.fridge);
    return Object.assign({}, b, {
      finish: finish,
      range: keep(BY_RANGES, b.range, "r36"),
      cooktop: keep(BY_COOKTOPS, b.cooktop, "c36"),
      oven: keep(BY_OVENS, b.oven, "none"),
      hood: keep(BY_HOODS, b.hood, "none"),
      fridge: (cur && cur.collection === "freestanding" && finish !== "ss") ? "none" : keep(BY_FRIDGES, b.fridge, "none"),
      dishwasher: keep(BY_DISHWASHERS, b.dishwasher, "none"),
      microwave: keep(BY_MICROWAVES, b.microwave, "none")
    });
  }

  function suggestHood(cookId) {
    var w = parseInt(String(cookId).replace(/\D/g, ""), 10);
    if (w >= 60) return "h60";
    if (w >= 48) return "h48";
    if (w >= 36) return "h36";
    return "h30";
  }

  var HERO_RANGE = byId(BY_RANGES, "r48");
  var HERO_COOKTOP = byId(BY_COOKTOPS, "c36");
  var HERO_OVEN_SINGLE = byId(BY_OVENS, "single");
  var HERO_OVEN_DOUBLE = byId(BY_OVENS, "double");

  /* ── State ────────────────────────────────────────────── */

  var DEFAULT = { finish: "ss", path: "range", range: "r36", cooktop: "c36", oven: "double", hood: "h36", fridge: "fpair", dishwasher: "dw", microwave: "mw24" };
  var RESET = { finish: "ss", path: "range", range: "none", cooktop: "none", oven: "none", hood: "none", fridge: "none", dishwasher: "none", microwave: "none" };

  var build = Object.assign({}, DEFAULT);
  var index = 0;
  var cartOpen = false;
  /* Per-step category drill-down (ventilation, refrigeration). */
  var ui = { hood: { active: null, detail: false }, fridge: { active: null, detail: false } };

  function steps() {
    var cooking = build.path === "range"
      ? [{ id: "range", label: "Range" }]
      : [{ id: "cooktop", label: "Cooktop" }, { id: "oven", label: "Wall oven" }];
    return [{ id: "finish", label: "Finish" }, { id: "path", label: "Cooking" }]
      .concat(cooking)
      .concat([
        { id: "hood", label: "Ventilation" },
        { id: "fridge", label: "Refrigeration" },
        { id: "dishwasher", label: "Dishwasher" },
        { id: "microwave", label: "Microwave" },
        { id: "review", label: "Review" }
      ]);
  }

  /* ── Selection model ────────────────────────────────────── */

  function selections() {
    var list = [];
    function push(slot, opt) {
      if (!opt || opt.id === "none") return;
      var v = variant(opt, build.finish);
      if (!v) return;
      list.push({ slot: slot, name: opt.name, finish: FIN_LABEL[v.finish], price: v.price, url: v.url, img: v.img, imgs: v.imgs });
    }
    if (build.path === "range") push("range", byId(BY_RANGES, build.range));
    else {
      push("cooktop", byId(BY_COOKTOPS, build.cooktop));
      push("oven", byId(BY_OVENS, build.oven));
    }
    push("hood", byId(BY_HOODS, build.hood));
    push("fridge", byId(BY_FRIDGES, build.fridge));
    push("dishwasher", byId(BY_DISHWASHERS, build.dishwasher));
    push("microwave", byId(BY_MICROWAVES, build.microwave));
    return list;
  }
  function total(items) {
    return items.reduce(function (s, i) { return s + i.price; }, 0);
  }

  /* ── Fragments ────────────────────────────────────────── */

  function optionRow(opt, slot, activeId) {
    var v = variant(opt, build.finish);
    var forceLight = opt.collection === "hoodInserts" || opt.collection === "underCab" || opt.collection === "wallHoods";
    var dark = v && (v.finish === "bs" || v.finish === "orion" || forceLight) ? " is-bs" : "";
    var thumb = "";
    if (v && v.imgs) {
      thumb = '<span class="by-opt-thumb by-opt-thumb-multi' + dark + '">' +
        v.imgs.map(function (f) { return '<span style="background-image:url(' + esc(img(f)) + ')"></span>'; }).join("") + "</span>";
    } else if (v) {
      thumb = '<span class="by-opt-thumb' + dark + '" style="background-image:url(' + esc(img(v.img)) + ')"></span>';
    }
    return '<div class="by-opt' + (activeId === opt.id ? " is-active" : "") + '">' +
      '<button type="button" class="by-opt-hit" data-act="pick" data-slot="' + esc(slot) + '" data-id="' + esc(opt.id) + '">' +
        thumb +
        '<span class="by-opt-body">' +
          '<span class="by-opt-name">' + esc(opt.name) + (opt.popular ? '<span class="by-badge">Most chosen</span>' : "") + "</span>" +
          '<span class="by-opt-meta">' + esc(opt.blurb) + "</span>" +
          (v && !v.exact ? '<span class="by-opt-note">Available in ' + esc(FIN_LABEL[v.finish]) + "</span>" : "") +
        "</span>" +
        '<span class="by-opt-price">' + (v ? fmt(v.price) : "—") + "</span>" +
      "</button>" +
      (v ? '<a class="by-opt-link" href="' + esc(url(v.url)) + '" target="_blank" rel="noopener">View product ' + ICON.arrow + "</a>" : "") +
      "</div>";
  }

  function visual(opt, caption) {
    var v = variant(opt, build.finish);
    if (!v) return '<div class="by-visual-empty"><span class="by-visual-label">' + esc(caption) + "</span></div>";
    var media = v.imgs
      ? '<span class="by-visual-multi">' + v.imgs.map(function (f) { return '<span class="by-visual-img" style="background-image:url(' + esc(img(f)) + ')"></span>'; }).join("") + "</span>"
      : '<span class="by-visual-img" style="background-image:url(' + esc(img(v.img)) + ')"></span>';
    var label = esc(opt.name) + (opt.collection !== "dropIns" ? " · " + esc(FIN_LABEL[v.finish]) : "");
    return '<a class="by-visual-inner" href="' + esc(url(v.url)) + '" target="_blank" rel="noopener">' + media +
      '<span class="by-visual-label">' + label + "</span></a>";
  }

  function visualPair(opts, caption) {
    var vs = opts.map(function (o) { return variant(o, build.finish); }).filter(Boolean);
    if (!vs.length) return '<div class="by-visual-empty"><span class="by-visual-label">' + esc(caption) + "</span></div>";
    return '<div class="by-visual-inner"><div class="by-visual-pair">' +
      vs.map(function (v) {
        return '<a class="by-visual-pair-item" href="' + esc(url(v.url)) + '" target="_blank" rel="noopener">' +
          '<span class="by-visual-img" style="background-image:url(' + esc(img(v.img)) + ')"></span></a>';
      }).join("") +
      '</div><span class="by-visual-label">' + esc(caption) + "</span></div>";
  }

  function stepShell(o) {
    return '<div class="by-layout">' +
      '<div class="by-visual">' + o.visual + "</div>" +
      '<div class="by-options">' +
        '<div class="by-head">' +
          (o.eyebrow ? '<span class="by-eyebrow">' + esc(o.eyebrow) + "</span>" : "") +
          '<h2 class="by-title">' + esc(o.title) + "</h2>" +
          (o.sub ? '<p class="by-sub">' + esc(o.sub) + "</p>" : "") +
          (o.shopHref ? '<a class="by-shop-link" href="' + esc(url(o.shopHref)) + '" target="_blank" rel="noopener">' + esc(o.shopLabel) + " " + ICON.arrow + "</a>" : "") +
        "</div>" + o.body +
      "</div></div>";
  }

  /* Shared drill-down used by ventilation and refrigeration. */
  function catStage(key, slot, categories, skipOpt, backLabel) {
    var st = ui[key];
    var activeCat = null;
    for (var i = 0; i < categories.length; i++) if (categories[i].id === st.active) activeCat = categories[i];
    if (!activeCat) {
      /* Land on whichever category holds the current selection. */
      for (var j = 0; j < categories.length; j++) {
        if (categories[j].list.some(function (o) { return o.id === build[slot]; })) { activeCat = categories[j]; st.active = categories[j].id; }
      }
    }
    var cards = categories.map(function (c) {
      var v0 = variant(c.list[0], build.finish);
      var isActive = build[slot] !== "none" && c.list.some(function (o) { return o.id === build[slot]; });
      return '<button type="button" class="by-vent-card' + (isActive ? " is-active" : "") + '" data-act="cat-open" data-key="' + esc(key) + '" data-cat="' + esc(c.id) + '">' +
        (v0 ? '<span class="by-vent-card-thumb is-bs" style="background-image:url(' + esc(img(v0.img)) + ')"></span>' : "") +
        '<span class="by-vent-card-name">' + esc(c.label) + "</span>" +
        '<span class="by-vent-card-count">' + c.list.length + " size" + (c.list.length === 1 ? "" : "s") + "</span>" +
        "</button>";
    }).join("");

    return '<div class="by-vent-stage"><div class="by-vent-track' + (st.detail ? " is-detail" : "") + '" data-track="' + esc(key) + '">' +
      '<div class="by-vent-pane by-vent-pane-cats"' + (st.detail ? ' aria-hidden="true"' : "") + '>' +
        '<div class="by-vent-cards">' + cards + "</div>" +
        '<div class="by-opt-group by-group-gap">' + optionRow(skipOpt, slot, build[slot]) + "</div>" +
      "</div>" +
      '<div class="by-vent-pane by-vent-pane-detail"' + (st.detail ? "" : ' aria-hidden="true"') + ">" +
        (activeCat
          ? '<button type="button" class="by-vent-back" data-act="cat-back" data-key="' + esc(key) + '">← ' + esc(backLabel) + "</button>" +
            '<div class="by-group-label">' + esc(activeCat.label) + '<a href="' + esc(url(activeCat.shopHref)) + '" target="_blank" rel="noopener">Shop →</a></div>' +
            '<div class="by-opt-group">' + activeCat.list.map(function (o) { return optionRow(o, slot, build[slot]); }).join("") + "</div>"
          : "") +
      "</div></div></div>";
  }

  /* ── Steps ────────────────────────────────────────────── */

  function stepFinish() {
    return stepShell({
      eyebrow: "Step one", title: "Choose your finish.",
      sub: "Your finish carries across every appliance in the suite. Ranges, hoods, refrigeration, dishwashers and microwaves are all built on one platform and finished to match.",
      shopHref: BY_COLLECTIONS.all, shopLabel: "Browse all indoor products",
      visual: visual(HERO_RANGE, "Suite finish"),
      body: '<div class="by-opt-group">' + FINISHES.map(function (f) {
        return '<button type="button" class="by-swatch-btn' + (build.finish === f.id ? " is-active" : "") + '" data-act="finish" data-id="' + esc(f.id) + '">' +
          '<span class="by-swatch" style="background:' + esc(f.swatch) + '"></span>' +
          '<span><span class="by-opt-name">' + esc(f.name) + '</span><span class="by-opt-meta">' + esc(f.meta) + "</span></span></button>";
      }).join("") + "</div>" +
      '<p class="by-foot-note">VEGA and ORION are available on ranges, cooktops, wall ovens, the dishwasher, microwaves, and select refrigeration. Other categories will show in Stainless or Black Stainless.</p>'
    });
  }

  function stepPath() {
    var paths = [
      { id: "range", name: "A freestanding range", blurb: "One appliance: chef-grade induction hob over dual convection ovens. The classic centerpiece.", tag: "Most kitchens", shots: [HERO_RANGE] },
      { id: "builtin", name: "A cooktop with a wall oven", blurb: "Split the two. Induction built into the counter, oven at eye level or under the island.", shots: [HERO_COOKTOP, HERO_OVEN_SINGLE, HERO_OVEN_DOUBLE] }
    ];
    return stepShell({
      eyebrow: "Step two", title: "Start with the centerpiece.",
      sub: "Everything else in the suite matches back to your cooking surface — width, finish, and ventilation all follow from this choice.",
      shopHref: BY_COLLECTIONS.ranges, shopLabel: "Shop Luxury Series ranges",
      visual: build.path === "range" ? visual(HERO_RANGE, "Cooking") : visualPair([HERO_COOKTOP, HERO_OVEN_DOUBLE], "36″ Induction Cooktop + 30″ Double Wall Oven"),
      body: '<div class="by-path-grid">' + paths.map(function (p) {
        var shots = p.shots.map(function (o) {
          var v = variant(o, build.finish);
          if (!v) return "";
          var dark = (v.finish === "bs" || v.finish === "orion") ? " is-bs" : "";
          return '<span class="by-path-shot' + dark + '" style="background-image:url(' + esc(img(v.img)) + ')"></span>';
        }).join("");
        return '<button type="button" class="by-path-btn' + (build.path === p.id ? " is-active" : "") + '" data-act="path" data-id="' + esc(p.id) + '">' +
          (p.tag ? '<span class="by-badge by-badge-abs">' + esc(p.tag) + "</span>" : "") +
          '<span class="by-path-shots">' + shots + "</span>" +
          '<span class="by-path-name">' + esc(p.name) + "</span>" +
          '<span class="by-path-blurb">' + esc(p.blurb) + "</span></button>";
      }).join("") + "</div>"
    });
  }

  function simpleStep(o) {
    return stepShell({
      eyebrow: o.eyebrow, title: o.title, sub: o.sub, shopHref: o.shopHref, shopLabel: o.shopLabel,
      visual: visual(byId(o.list, build[o.slot]), o.caption),
      body: '<div class="by-opt-group">' + noneLast(o.list).map(function (x) { return optionRow(x, o.slot, build[o.slot]); }).join("") + "</div>" +
        (o.note || "")
    });
  }

  function stepRange() {
    return simpleStep({
      slot: "range", list: BY_RANGES, caption: "Range", eyebrow: "Cooking", title: "Pick your range.",
      sub: "One platform in four widths. Identical internals, Pro Pizza mode at 660°F, and a lifetime-backed warranty in every size.",
      shopHref: BY_COLLECTIONS.ranges, shopLabel: "Shop all induction ranges"
    });
  }
  function stepCooktop() {
    return simpleStep({
      slot: "cooktop", list: BY_COOKTOPS, caption: "Cooktop", eyebrow: "Cooking", title: "Pick your cooktop.",
      sub: "Knob-front rangetops in three widths, or a flush top-touch drop-in that disappears into the counter.",
      shopHref: BY_COLLECTIONS.cooktops, shopLabel: "Shop induction cooktops",
      note: '<p class="by-foot-note">Looking for flush drop-ins only? <a href="' + esc(url(BY_COLLECTIONS.dropIns)) + '" target="_blank" rel="noopener">Shop induction drop-ins</a></p>'
    });
  }
  function stepOven() {
    return simpleStep({
      slot: "oven", list: BY_OVENS, caption: "Wall oven", eyebrow: "Cooking", title: "Add the wall oven.",
      sub: "A wall oven wants a cooktop. One 30″ built-in platform in two configurations — true European convection, air fry, dehydrate, proof, and Pro Pizza.",
      shopHref: BY_COLLECTIONS.wallOvens, shopLabel: "Shop wall ovens"
    });
  }
  function stepDishwasher() {
    return simpleStep({
      slot: "dishwasher", list: BY_DISHWASHERS, caption: "Dishwasher", eyebrow: "Cleaning", title: "Add the dishwasher.",
      sub: "Third rack standard, whisper-quiet operation, and a panel-ready front if you want it to disappear into the run.",
      shopHref: BY_COLLECTIONS.dishwashers, shopLabel: "Shop dishwashers"
    });
  }
  function stepMicrowave() {
    return simpleStep({
      slot: "microwave", list: BY_MICROWAVES, caption: "Microwave", eyebrow: "Finishing touch", title: "Cap it off with a microwave.",
      sub: "Drawer microwaves install below the counter, freeing the wall above your cooktop for ventilation and keeping the sightline clean.",
      shopHref: BY_COLLECTIONS.microwaves, shopLabel: "Shop microwaves"
    });
  }

  function stepHood() {
    var cookIdRaw = build.path === "range" ? build.range : build.cooktop;
    var cookWidth = cookIdRaw === "none" ? null : parseInt(String(cookIdRaw).replace(/\D/g, ""), 10);
    var categories = [
      { id: "wall", label: "Wall-mounted", list: where(BY_HOODS, "collection", "wallHoods"), shopHref: BY_COLLECTIONS.wallHoods },
      { id: "inserts", label: "Inserts", list: where(BY_HOODS, "collection", "hoodInserts"), shopHref: BY_COLLECTIONS.hoodInserts },
      { id: "under", label: "Under cabinet", list: where(BY_HOODS, "collection", "underCab"), shopHref: BY_COLLECTIONS.underCab }
    ];
    return stepShell({
      eyebrow: "Ventilation", title: "Match the ventilation.",
      sub: (cookWidth ? "Your cooking surface is " + cookWidth + "″. Match the hood to that width — or wider — for full capture." : "Pick a cooking surface first for a matched suggestion, or browse all formats now.") + " Three mounting formats, all in matching finishes.",
      shopHref: BY_COLLECTIONS.hoods, shopLabel: "Shop all range hoods",
      visual: visual(byId(BY_HOODS, build.hood), "Ventilation"),
      body: catStage("hood", "hood", categories, BY_HOODS[0], "All ventilation types")
    });
  }

  function stepFridge() {
    var categories = [
      { id: "column", label: "Column", list: where(BY_FRIDGES, "collection", "columns"), shopHref: BY_COLLECTIONS.columns },
      { id: "builtin", label: "Built-In", list: where(BY_FRIDGES, "collection", "builtin"), shopHref: BY_COLLECTIONS.builtin }
    ];
    /* Pro Free Standing is Stainless-only. */
    if (build.finish === "ss") categories.push({ id: "freestanding", label: "Pro Free Standing", list: where(BY_FRIDGES, "collection", "freestanding"), shopHref: BY_COLLECTIONS.freestanding });
    categories.push({ id: "frenchdoor", label: "French Door", list: where(BY_FRIDGES, "collection", "frenchDoor"), shopHref: BY_COLLECTIONS.frenchDoor });

    return stepShell({
      eyebrow: "Refrigeration", title: "Choose refrigeration.",
      sub: "Column, built-in combo, freestanding, or French door — four refrigeration formats, all finished to match the rest of the suite.",
      shopHref: BY_COLLECTIONS.fridge, shopLabel: "Shop all refrigeration",
      visual: visual(byId(BY_FRIDGES, build.fridge), "Refrigeration"),
      body: catStage("fridge", "fridge", categories, BY_FRIDGES[0], "All refrigeration types") +
        '<p class="by-foot-note">Panel-ready fronts are available on every column, built-in, and French door model — clad the door in your own cabinetry.</p>'
    });
  }

  function stepReview() {
    var items = selections();
    return '<div class="by-review">' +
      '<div class="by-review-head">' +
        '<span class="by-eyebrow">Your suite</span>' +
        '<h2 class="by-title">Here&rsquo;s the kitchen you built.</h2>' +
        '<p class="by-sub">' + items.length + " appliances in " + esc(FIN_LABEL[build.finish]) + ". Every line links to its product page — open them side by side, or talk to a specialist.</p>" +
      "</div>" +
      '<div class="by-review-grid">' + items.map(function (it) {
        var media = it.imgs
          ? '<span class="by-rev-img by-rev-img-multi">' + it.imgs.map(function (f) { return '<span style="background-image:url(' + esc(img(f)) + ')"></span>'; }).join("") + "</span>"
          : '<span class="by-rev-img"><span style="background-image:url(' + esc(img(it.img)) + ')"></span></span>';
        return '<div class="by-rev-card">' +
          '<button type="button" class="by-rev-remove" aria-label="Remove ' + esc(it.name) + '" data-act="remove" data-slot="' + esc(it.slot) + '">✕</button>' +
          '<a class="by-rev-card-link" href="' + esc(url(it.url)) + '" target="_blank" rel="noopener">' + media +
            '<span class="by-rev-name">' + esc(it.name) + "</span>" +
            '<span class="by-rev-meta">' + esc(it.finish) + "</span>" +
            '<span class="by-rev-price">' + fmt(it.price) + "</span>" +
            '<span class="by-rev-cta">View product ' + ICON.arrow + "</span></a></div>";
      }).join("") + "</div>" +
      '<div class="by-review-foot">' +
        '<div class="by-total"><span class="by-total-lbl">Suite total</span><span class="by-total-num tabular">' + fmt(total(items)) + "</span></div>" +
        '<div class="by-review-actions">' +
          '<a href="' + esc(url("/cart")) + '" class="by-btn by-btn-primary">Checkout ' + ICON.arrow + "</a>" +
          '<a href="tel:+17758987562" class="by-btn by-btn-secondary">Talk to a specialist</a>' +
          '<a href="' + esc(url("/pages/locations")) + '" class="by-btn by-btn-secondary">Find a showroom</a>' +
          '<button type="button" class="by-reset-link" data-act="reset">Start over</button>' +
        "</div>" +
      "</div>" +
      '<ul class="by-trust">' +
        "<li>" + ICON.shield + " Lifetime warranty on every appliance in the suite</li>" +
        "<li>" + ICON.bolt + " All-electric — federal and state incentives may apply</li>" +
      "</ul></div>";
  }

  var RENDERERS = {
    finish: stepFinish, path: stepPath, range: stepRange, cooktop: stepCooktop, oven: stepOven,
    hood: stepHood, fridge: stepFridge, dishwasher: stepDishwasher, microwave: stepMicrowave, review: stepReview
  };

  /* ── Shell ────────────────────────────────────────────── */

  function render() {
    var list = steps();
    if (index > list.length - 1) index = list.length - 1;
    var isReview = index === list.length - 1;
    var items = selections();
    var sum = total(items);

    var stepper = list.map(function (s, i) {
      var cls = i === index ? " is-active" : (i < index ? " is-done" : "");
      return '<button type="button" class="by-pip' + cls + '" data-act="go" data-i="' + i + '">' +
        '<span class="by-pip-circle">' + (i < index ? "✓" : i + 1) + "</span>" +
        '<span class="by-pip-label">' + esc(s.label) + "</span></button>" +
        (i < list.length - 1 ? '<span class="by-connector' + (i < index ? " is-done" : "") + '"></span>' : "");
    }).join("");

    var cartBody = items.length === 0
      ? '<p class="by-cart-empty">Nothing chosen yet — selections appear here as you build.</p>'
      : '<div class="by-cart-list">' + items.map(function (it) {
          return '<div class="by-cart-item">' +
            '<a class="by-cart-item-link" href="' + esc(url(it.url)) + '" target="_blank" rel="noopener">' +
              '<span class="by-cart-item-img"><span style="background-image:url(' + esc(img(it.imgs ? it.imgs[0] : it.img)) + ')"></span></span>' +
              '<span class="by-cart-item-body"><span class="by-cart-item-name">' + esc(it.name) + '</span><span class="by-cart-item-meta">' + esc(it.finish) + "</span></span>" +
              '<span class="by-cart-item-price">' + fmt(it.price) + "</span></a>" +
            '<button type="button" class="by-cart-item-remove" aria-label="Remove ' + esc(it.name) + '" data-act="remove" data-slot="' + esc(it.slot) + '">✕</button></div>';
        }).join("") + "</div>";

    root.innerHTML =
      '<div class="by-root">' +
        '<div class="by-page-head"><div class="by-container">' +
          '<span class="by-eyebrow">Build your own kitchen suite</span>' +
          '<h1 class="by-page-title">One kitchen. <em>One</em> finish.</h1>' +
          '<p class="by-page-sub">Work through the suite in the order a kitchen comes together — cooking first, then ventilation, refrigeration, cleaning. Every choice links straight to the product.</p>' +
        "</div></div>" +
        '<div class="by-stepper-wrap"><div class="by-container"><div class="by-stepper">' + stepper + "</div></div></div>" +
        '<div class="by-body"><div class="by-container">' + RENDERERS[list[index].id]() + "</div></div>" +
        '<div class="by-bar"><div class="by-bar-inner">' +
          '<div class="by-bar-total"><span class="by-bar-lbl">Suite total · ' + esc(FIN_LABEL[build.finish]) + '</span><span class="by-bar-price tabular">' + fmt(sum) + "</span></div>" +
          '<div class="by-bar-actions">' +
            '<button type="button" class="by-btn by-btn-ghost by-bar-cart" data-act="cart-toggle" aria-expanded="' + (cartOpen ? "true" : "false") + '">Your suite (' + items.length + ")</button>" +
            (index > 0 ? '<button type="button" class="by-btn by-btn-secondary" data-act="back">Back</button>' : "") +
            (isReview
              ? '<a href="tel:+17758987562" class="by-btn by-btn-primary">Talk to a specialist ' + ICON.arrow + "</a>"
              : '<button type="button" class="by-btn by-btn-primary" data-act="next">Continue ' + ICON.arrow + "</button>") +
          "</div>" +
        "</div></div>" +
        '<div class="by-cart-drawer' + (cartOpen ? " is-open" : "") + '">' +
          '<div class="by-cart-scrim" data-act="cart-close"></div>' +
          '<div class="by-cart-panel" role="dialog" aria-label="Your suite so far">' +
            '<div class="by-cart-head"><span class="by-eyebrow">Your suite so far</span>' +
              '<button type="button" class="by-cart-close" data-act="cart-close" aria-label="Close">✕</button></div>' +
            (items.length ? '<button type="button" class="by-cart-reset" data-act="reset">Start over — reset kitchen suite</button>' : "") +
            cartBody +
            '<div class="by-cart-foot"><span class="by-cart-total-lbl">Suite total</span><span class="by-cart-total-num tabular">' + fmt(sum) + "</span></div>" +
            '<a href="' + esc(url("/cart")) + '" class="by-btn by-btn-primary by-cart-checkout">Checkout ' + ICON.arrow + "</a>" +
          "</div>" +
        "</div>" +
      "</div>";
  }

  function go(i) {
    var list = steps();
    index = Math.max(0, Math.min(i, list.length - 1));
    render();
    root.scrollIntoView({ block: "start" });
  }

  /* ── Events ──────────────────────────────────────────── */

  root.addEventListener("click", function (e) {
    var el = e.target.closest("[data-act]");
    if (!el || !root.contains(el)) return;
    var act = el.getAttribute("data-act");

    switch (act) {
      case "finish":
        build = applyFinish(build, el.getAttribute("data-id"));
        /* A cleared selection may have emptied the open category. */
        ui.hood.detail = false; ui.fridge.detail = false;
        render();
        break;

      case "path":
        build = Object.assign({}, build, { path: el.getAttribute("data-id") });
        render();
        break;

      case "pick": {
        var slot = el.getAttribute("data-slot");
        var id = el.getAttribute("data-id");
        var patch = {};
        patch[slot] = id;
        /* Choosing a cooking surface re-suggests a width-matched hood. */
        if (slot === "range" || slot === "cooktop") patch.hood = id === "none" ? "none" : suggestHood(id);
        build = Object.assign({}, build, patch);
        render();
        break;
      }

      case "remove": {
        var cleared = {};
        cleared[el.getAttribute("data-slot")] = "none";
        build = Object.assign({}, build, cleared);
        render();
        break;
      }

      case "cat-open": {
        var k = el.getAttribute("data-key");
        ui[k].active = el.getAttribute("data-cat");
        ui[k].detail = false;
        render();
        /* Paint the cats pane first so the track can transition into detail. */
        var track = root.querySelector('[data-track="' + k + '"]');
        if (track) requestAnimationFrame(function () { ui[k].detail = true; track.classList.add("is-detail"); });
        break;
      }

      case "cat-back": {
        var k2 = el.getAttribute("data-key");
        ui[k2].detail = false;
        var t2 = root.querySelector('[data-track="' + k2 + '"]');
        if (t2) t2.classList.remove("is-detail");
        break;
      }

      case "go": go(parseInt(el.getAttribute("data-i"), 10)); break;
      case "next": go(index + 1); break;
      case "back": go(index - 1); break;
      case "cart-toggle": cartOpen = !cartOpen; render(); break;
      case "cart-close": cartOpen = false; render(); break;

      case "reset":
        if (window.confirm("Reset your kitchen suite? This clears every selection and starts over.")) {
          build = Object.assign({}, RESET);
          index = 0;
          cartOpen = false;
          ui = { hood: { active: null, detail: false }, fridge: { active: null, detail: false } };
          render();
          root.scrollIntoView({ block: "start" });
        }
        break;
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && cartOpen) { cartOpen = false; render(); }
  });

  render();
})();
